// Maps a national-team code to its ISO 3166-1 alpha-2 code.
// National teams use FIFA-style trigrams (BRA, FRA, …) which don't map 1:1 to the
// ISO-3166 alpha-2 codes flag libraries need, so we keep an explicit table.
// A few seed codes are non-standard (SOU = South Africa, BOS = Bosnia) and are
// aliased to the correct ISO-2 alongside the FIFA-standard trigram.
// Codes not present here (e.g. Brasileirão clubs) fall back to the text chip in the UI.

const CODE_TO_ISO2: Record<string, string> = {
  // South America
  BRA: 'BR', ARG: 'AR', URU: 'UY', COL: 'CO', CHI: 'CL', PER: 'PE',
  ECU: 'EC', PAR: 'PY', VEN: 'VE', BOL: 'BO',
  // Europe
  FRA: 'FR', ENG: 'GB', ESP: 'ES', GER: 'DE', POR: 'PT', ITA: 'IT',
  NED: 'NL', BEL: 'BE', CRO: 'HR', SRB: 'RS', SUI: 'CH', POL: 'PL',
  DEN: 'DK', SWE: 'SE', NOR: 'NO', AUT: 'AT', UKR: 'UA', SCO: 'GB',
  WAL: 'GB', CZE: 'CZ', TUR: 'TR', GRE: 'GR', RUS: 'RU', HUN: 'HU',
  ROU: 'RO', SVK: 'SK', SVN: 'SI', IRL: 'IE', ISL: 'IS', FIN: 'FI',
  BIH: 'BA', BOS: 'BA', ALB: 'AL', MKD: 'MK', GEO: 'GE',
  // North & Central America
  USA: 'US', MEX: 'MX', CAN: 'CA', CRC: 'CR', PAN: 'PA', HON: 'HN',
  JAM: 'JM',
  // Africa
  MAR: 'MA', SEN: 'SN', TUN: 'TN', ALG: 'DZ', EGY: 'EG', NGA: 'NG',
  GHA: 'GH', CMR: 'CM', CIV: 'CI', RSA: 'ZA', SOU: 'ZA', MLI: 'ML',
  // Asia & Oceania
  JPN: 'JP', KOR: 'KR', AUS: 'AU', KSA: 'SA', IRN: 'IR', QAT: 'QA',
  UAE: 'AE', IRQ: 'IQ', UZB: 'UZ', CHN: 'CN', NZL: 'NZ',

  // Alternate name-prefix codes used by the live data feed (e.g. SWItzerland, NETherlands)
  // alongside the FIFA trigrams above.
  SWI: 'CH', MOR: 'MA', HAI: 'HT', CUW: 'CW', JAP: 'JP', NET: 'NL',
  IVO: 'CI', SPA: 'ES', CAP: 'CV', SAU: 'SA', ZEA: 'NZ', JOR: 'JO',
  CON: 'CD', SER: 'RS', CAM: 'CM', COS: 'CR',
}

/** Resolves a national-team code (FIFA trigram or ISO-2) to its ISO-2 alpha code, or null. */
export function countryIso2(code: string | undefined): string | null {
  if (!code) return null
  const upper = code.toUpperCase()
  if (CODE_TO_ISO2[upper]) return CODE_TO_ISO2[upper]
  // Already a valid ISO-2 alpha code (e.g. data that ships ISO codes directly).
  if (/^[A-Z]{2}$/.test(upper)) return upper
  return null
}

/**
 * Returns the flag emoji for a team code, or null when no mapping exists.
 * Note: Windows has no flag-emoji glyphs and renders these as the bare letters,
 * so prefer the SVG-backed `<Flag>` component for UI. Kept for non-visual use.
 */
export function countryFlag(code: string | undefined): string | null {
  const iso2 = countryIso2(code)
  if (!iso2 || iso2.length !== 2) return null
  // Regional-indicator symbols: 'A' (0x41) → 0x1F1E6.
  return String.fromCodePoint(
    ...iso2.split('').map((c) => 0x1f1e6 + (c.charCodeAt(0) - 0x41)),
  )
}
